/**
 * Headline box: one dominant power state only.
 * Replaces Net Power. Installer-native labels; direction in label; always positive kW.
 * Visual hierarchy: Class 1 (dependency) stronger; Class 2 green, less emphasis; Class 3 neutral; Self-Supplied muted.
 */

import type { DominantPowerStateResult } from '@/hooks/useDominantPowerState';

interface DominantPowerStateCardProps {
  /** Result from useDominantPowerState (label + optional kw). */
  state: DominantPowerStateResult;
  /** Optional: when connecting/loading, show muted placeholder */
  isConnecting?: boolean;
  className?: string;
}

/** Class 1 = dependency (stronger); Class 2 = export (green, less); Class 3 = battery (neutral); Self-Supplied = muted. */
function getStateStyles(state: DominantPowerStateResult): { color: string; emphasis: string } {
  switch (state.label) {
    case 'Generator Running':
    case 'Importing from Grid':
      return { color: 'text-wt-amber-primary', emphasis: 'font-semibold' };
    case 'Exporting to Grid':
      return { color: 'text-wt-green-primary', emphasis: 'font-medium' };
    case 'Running on Battery':
    case 'Charging Battery':
      return {
        color: state.label === 'Running on Battery' ? 'text-destructive' : 'text-wt-green-primary',
        emphasis: 'font-medium',
      };
    default:
      return { color: 'text-wt-text-secondary', emphasis: 'font-medium' };
  }
}

export function DominantPowerStateCard({ state, isConnecting = false, className = '' }: DominantPowerStateCardProps) {
  const { color, emphasis } = getStateStyles(state);
  const hasKw = state.label !== 'Self-Supplied' && 'kw' in state;

  if (isConnecting) {
    return (
      <div className={className}>
        <label className="label text-wt-text-secondary block mb-2">Dominant state</label>
        <div className="font-mono-numeric text-2xl font-semibold text-secondary">—</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <label className="label text-wt-text-secondary block mb-2">Dominant state</label>
      <div className={`font-mono-numeric text-2xl mb-1 ${emphasis} ${color}`}>
        {state.label}
      </div>
      {hasKw && 'kw' in state && (
        <div className={`text-[13px] ${color}`}>
          {state.kw.toFixed(1)} kW
        </div>
      )}
      {state.label === 'Self-Supplied' && (
        <div className="text-[13px] text-wt-text-secondary">
          Generation meets load
        </div>
      )}
      {state.subLabel && (
        <div className="text-[13px] text-wt-amber-primary mt-0.5">
          {state.subLabel}
        </div>
      )}
    </div>
  );
}
